import { IJobMeta } from '../../../utils/BackgroundJobs';
import React, { useContext, useState } from 'react';
import AutomaticDispatchResultWorkspace from '../../../views/product/AutomaticDispatchResultWorkspace';
import { InfoButton } from '../../connectedForm/Button';
import ModalContext from '../../Context/ModalContext';
import AutomaticDispatchResultWorkspaceQuery from '../../../graphql/queries/product/AutomaticDispatchResultWorkspaceQuery.graphql';
import {
  AlertType,
  FilterOperation,
  IAutomaticDispatchResult,
  IAutomaticDispatchResultWorkspaceQueryResult,
  IAutomaticDispatchResultWorkspaceQueryVariables,
  IDispatchInstruction,
  LabelPrintOption,
  Maybe,
  ReportType,
} from '../../../graphql/schema/p4types';
import { useDispatchPrintModals } from './useDispatchPrintModals';
import Notification from '../../Notification';
import { useAllPagesQuery } from '../../../graphql/containers/useAllPagesQuery';
import { useP4ToastController } from '../../../views/system/NotificationsArea';
import { useToastController } from '@fluentui/react-components';
import AppContext from '../../Context/AppContext';
import FluentModal from '../../modals/FluentModal';
import { useSelector } from 'react-redux';
import { IReduxStore } from '../../../@types/reduxTypes';
import { SpecialReportType, useP4DocumentController } from '../../document/DocumentProvider';

const DispatchResultNotification = ({
  data,
  job,
  dispatchResultToastId,
}: {
  data?: IAutomaticDispatchResultWorkspaceQueryResult;
  job: IJobMeta;
  dispatchResultToastId: string;
}) => {
  const [resultModalOpen, setResultModalOpen] = useState(false);

  const { toasterId } = useContext(AppContext);
  const { dismissToast } = useToastController(toasterId);

  const { printDocumentActionEntities } = useP4DocumentController();
  const enableDocumentCarousel = useSelector((store: IReduxStore) => {
    return store.app.fullUserDetails?.flags?.documentCarouselEnabled ?? false;
  });

  const dispatchInstructions: { [id: number]: Maybe<IDispatchInstruction> } = {};
  const deliveryNoteDispatchInstructions: { [id: number]: Maybe<IDispatchInstruction> } = {};
  const conformityCertificateDispatchInstructions: { [id: number]: Maybe<IDispatchInstruction> } = {};

  const results = data?.listData?.edges?.map((edge) => edge?.node as IAutomaticDispatchResult) || [];
  const uniqueCustomerIds = new Set(results.map((value) => value.dispatchInstruction?.salesOrder?.customer?.id));
  const onlyCustomerId = uniqueCustomerIds.size === 1 ? uniqueCustomerIds.values().next().value : null;

  var promptLabels = false;

  for (const dispatchResult of results) {
    const dispatchInstruction = dispatchResult.dispatchInstruction;

    if (dispatchResult.success && dispatchInstruction) {
      const id = dispatchInstruction.id;

      if (!(id in dispatchInstructions)) {
        dispatchInstructions[id] = dispatchInstruction;
      }

      if (dispatchResult?.dispatchInstruction?.deliveryMethod?.carrier?.labelPrintOption === LabelPrintOption.Prompt) {
        promptLabels = true;
      }

      if (dispatchResult.promptDeliveryNote && !(id in deliveryNoteDispatchInstructions)) {
        deliveryNoteDispatchInstructions[id] = dispatchInstruction;
      }
      if (dispatchResult.promptConformityCertificate && !(id in conformityCertificateDispatchInstructions)) {
        conformityCertificateDispatchInstructions[id] = dispatchInstruction;
      }
    }
  }

  const [
    { setLabelModalState, setDeliveryNoteModalState, setConformityCertificateModalState, dispatchPrintModalOpen },
    printModals,
  ] = useDispatchPrintModals({
    multiValue: true,
    defaultLabelModalState: promptLabels
      ? {
          modalOpen: true,
          data: Object.values(dispatchInstructions) as Array<IDispatchInstruction>,
        }
      : undefined,
  });

  const notificationActionOptions: JSX.Element[] = [];

  notificationActionOptions.push(
    <InfoButton
      key={'resultsOption'}
      onClick={() => {
        setResultModalOpen(true);
      }}
    >
      View Results
    </InfoButton>
  );

  if (dispatchInstructions && Object.keys(dispatchInstructions).length > 0) {
    notificationActionOptions.push(
      <InfoButton
        key={'printLabels'}
        onClick={() => {
          const actionEntities = Object.values(dispatchInstructions) as Array<IDispatchInstruction>;
          if (enableDocumentCarousel) {
            printDocumentActionEntities(SpecialReportType.DispatchLabel, {
              actionEntities: actionEntities,
              include: true,
              filters: [],
            });
          } else {
            setLabelModalState({
              modalOpen: true,
              data: actionEntities,
            });
          }
        }}
      >
        Print Labels
      </InfoButton>
    );
  }
  if (deliveryNoteDispatchInstructions && Object.keys(deliveryNoteDispatchInstructions).length > 0) {
    notificationActionOptions.push(
      <InfoButton
        key={'printDeliveryNotes'}
        onClick={() => {
          const actionEntities = Object.values(deliveryNoteDispatchInstructions) as Array<IDispatchInstruction>;
          if (enableDocumentCarousel) {
            printDocumentActionEntities(ReportType.SalesDeliveryNote, {
              actionEntities: actionEntities,
              include: true,
              filters: [],
            });
          } else {
            setDeliveryNoteModalState({
              modalOpen: true,
              data: actionEntities,
              values: { customer: { id: onlyCustomerId } },
            });
          }
        }}
      >
        Print Delivery Notes
      </InfoButton>
    );

    notificationActionOptions.push(
      <InfoButton
        key={'printDeliveryNotesAndClose'}
        onClick={() => {
          const actionEntities = Object.values(deliveryNoteDispatchInstructions) as Array<IDispatchInstruction>;
          if (enableDocumentCarousel) {
            printDocumentActionEntities(ReportType.SalesDeliveryNote, {
              actionEntities: actionEntities,
              include: true,
              filters: [],
            });
            dismissToast(dispatchResultToastId);
          } else {
            setDeliveryNoteModalState({
              modalOpen: true,
              data: actionEntities,
              values: { customer: { id: onlyCustomerId } },
              dismissToast: () => dismissToast(dispatchResultToastId),
            });
          }
        }}
      >
        Print Delivery Notes & Close
      </InfoButton>
    );
  }
  if (conformityCertificateDispatchInstructions && Object.keys(conformityCertificateDispatchInstructions).length > 0) {
    notificationActionOptions.push(
      <InfoButton
        key={'printConformityCertificates'}
        onClick={() => {
          const actionEntities = Object.values(deliveryNoteDispatchInstructions) as Array<IDispatchInstruction>;
          if (enableDocumentCarousel) {
            printDocumentActionEntities(ReportType.ConformityCertificate, {
              actionEntities: actionEntities,
              include: true,
              filters: [],
            });
          } else {
            setConformityCertificateModalState({
              modalOpen: true,
              data: actionEntities,
              values: { customer: { id: onlyCustomerId } },
            });
          }
        }}
      >
        Print Conformity Certificates
      </InfoButton>
    );
  }

  return (
    <>
      {printModals}
      <FluentModal isOpen={resultModalOpen} styles={{ main: { minWidth: '800px' } }}>
        <ModalContext.Provider value={{ onClose: () => setResultModalOpen(false) }}>
          <div style={{ padding: '1em' }}>
            <AutomaticDispatchResultWorkspace
              embeddedView={true}
              predefinedFilters={[
                {
                  filterOperation: FilterOperation.Equals,
                  filterParameters: {
                    field: 'jobId',
                    value: job.jobId,
                  },
                },
              ]}
              viewScreenName="/AutomaticDispatchResults/Modal"
              removeAddButton={true}
              contentHeight={500}
              onBack={() => setResultModalOpen(false)}
              includeHeader={true}
            />
          </div>
        </ModalContext.Provider>
      </FluentModal>
      {!resultModalOpen && !dispatchPrintModalOpen ? (
        <Notification
          title={'Automatic Dispatch'}
          message={
            job?.recordsFailed ? `${job?.recordsFailed} order(s) failed to dispatch` : 'Orders have been dispatched'
          }
          actionButtons={notificationActionOptions}
          type={AlertType.Action}
        />
      ) : null}
    </>
  );
};

export const useDispatchResultToast = () => {
  const [fetchAutomaticDispatchResults] = useAllPagesQuery<
    IAutomaticDispatchResultWorkspaceQueryResult,
    IAutomaticDispatchResultWorkspaceQueryVariables
  >(AutomaticDispatchResultWorkspaceQuery);
  const { dispatchToast } = useP4ToastController();

  return (job: IJobMeta) => {
    fetchAutomaticDispatchResults({
      filters: [{ filterOperation: FilterOperation.Equals, filterParameters: { field: 'jobId', value: job.jobId } }],
    }).then((data) => {
      const dispatchResultToastId = `${job.jobId}-result`;

      dispatchToast(
        <DispatchResultNotification dispatchResultToastId={dispatchResultToastId} data={data} job={job} />,
        {
          title: 'Dispatch Result',
          type: AlertType.Action,
          toastId: dispatchResultToastId,
          intent: job.recordsFailed >= job.recordCount ? 'warning' : 'success',
        }
      );
    });
  };
};
